import styled from '@emotion/styled';
import type { MessageGenPhase } from '@trinity/chat';

interface PhaseBadgeProps {
  phase: MessageGenPhase;
  className?: string;
}

const PHASE_LABELS: Record<MessageGenPhase, string> = {
  idle: 'Ready',
  gathering_details: 'Gathering Details',
  awaiting_approval: 'Awaiting Approval',
  generating: 'Generating',
  messages_ready: 'Generated',
  prioritizing: 'Prioritizing',
  prioritized: 'Prioritized',
  curating: 'Curating',
  curated: 'Curated',
  error: 'Error',
};

type PhaseColorType = 'neutral' | 'warning' | 'primary' | 'success' | 'danger';

const PHASE_COLOR_TYPES: Record<MessageGenPhase, PhaseColorType> = {
  idle: 'neutral',
  gathering_details: 'neutral',
  awaiting_approval: 'warning',
  generating: 'primary',
  messages_ready: 'success',
  prioritizing: 'primary',
  prioritized: 'success',
  curating: 'primary',
  curated: 'success',
  error: 'danger',
};

export function PhaseBadge({ phase, className }: PhaseBadgeProps): React.JSX.Element {
  // Fallback to idle if phase is undefined or not found
  const colorType = PHASE_COLOR_TYPES[phase] || PHASE_COLOR_TYPES.idle;
  const label = PHASE_LABELS[phase] || phase || 'Unknown';

  return (
    <Badge $colorType={colorType} className={className}>
      {label}
    </Badge>
  );
}

const Badge = styled.span<{ $colorType: PhaseColorType }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  white-space: nowrap;
  background: ${(props) => {
    switch (props.$colorType) {
      case 'neutral':
        return props.theme.palette.gray.light2;
      case 'warning':
        return `${props.theme.palette.blue?.main || '#f59e0b'}15`;
      case 'primary':
        return props.theme.palette.sentiment.primary.lighter;
      case 'success':
        return `#10b98115`;
      case 'danger':
        return `#ef444415`;
    }
  }};
  color: ${(props) => {
    switch (props.$colorType) {
      case 'neutral':
        return props.theme.palette.gray.dark1;
      case 'warning':
        return props.theme.palette.blue?.main || '#f59e0b';
      case 'primary':
        return props.theme.palette.sentiment.primary.dark;
      case 'success':
        return '#10b981';
      case 'danger':
        return '#ef4444';
    }
  }};
`;
